import React, { useState, useRef, useEffect } from 'react';
import { X, Printer, FileText, HelpCircle, Key, ZoomIn, ZoomOut } from 'lucide-react';
import { KoreanTextModel } from '../../../../models';
// Placeholder components replacing removed super/pdf components
const UnitSheetView: React.FC<any> = () => (
    <div className="w-[794px] min-h-[400px] bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center text-slate-400 mx-auto my-4">
        PDF 시트 모듈이 제거되었습니다.
    </div>
);
const QuestionSheetView: React.FC<any> = () => (
    <div className="w-[794px] min-h-[400px] bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center text-slate-400 mx-auto my-4">
        PDF 문제 시트 모듈이 제거되었습니다.
    </div>
);
const DetailAnswerView: React.FC<any> = () => (
    <div className="w-[794px] min-h-[400px] bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center text-slate-400 mx-auto my-4">
        PDF 해설 시트 모듈이 제거되었습니다.
    </div>
);
const KoreanOverviewSheet: React.FC<any> = () => (
    <div className="w-[794px] min-h-[400px] bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center text-slate-400 mx-auto my-4">
        국어 개관 시트 모듈이 제거되었습니다.
    </div>
);
const KoreanAnalysisSheet: React.FC<any> = () => (
    <div className="w-[794px] min-h-[400px] bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center text-slate-400 mx-auto my-4">
        국어 분석 시트 모듈이 제거되었습니다.
    </div>
);

interface PdfSheetPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedParagraphs: KoreanTextModel[];
    questionSets: { [paragraphId: string]: any };
    bookTitle: string;
    subject?: string;
}

const PdfSheetPreviewModal: React.FC<PdfSheetPreviewModalProps> = ({
    isOpen,
    onClose,
    selectedParagraphs,
    questionSets,
    bookTitle,
    subject
}) => {
    const [viewMode, setViewMode] = useState<'concept' | 'koreanOverview' | 'koreanAnalysis' | 'question' | 'answer'>('concept');
    const [zoom, setZoom] = useState<number>(75);

    useEffect(() => {
        if (isOpen) {
            const isKorean = subject === '국어' || subject === 'korean';
            setViewMode(isKorean ? 'koreanOverview' : 'concept');
            pageCountsRef.current = {};
            setStartingPages({});
        }
    }, [isOpen, subject]);

    // Continuous page numbering states
    const pageCountsRef = useRef<{ [paragraphId: string]: number }>({});
    const [startingPages, setStartingPages] = useState<{ [paragraphId: string]: number }>({});

    const handlePageCount = (id: string, count: number) => {
        if (pageCountsRef.current[id] !== count) {
            pageCountsRef.current[id] = count;
            
            // Re-calculate all starting page numbers
            const newStarts: { [id: string]: number } = {};
            let currentStart = 1;
            selectedParagraphs.forEach(unit => {
                const qSet = questionSets[unit.id];
                if (viewMode === 'concept') {
                    newStarts[unit.id] = currentStart;
                    currentStart += pageCountsRef.current[unit.id] || 1;
                } else {
                    const qSetsList = Array.isArray(qSet) ? qSet : (qSet ? [qSet] : []);
                    if (qSetsList.length === 0) {
                        newStarts[unit.id] = currentStart;
                        currentStart += 1;
                    } else {
                        qSetsList.forEach((singleSet, sIdx) => {
                            const setKey = `${unit.id}_set_${sIdx}`;
                            newStarts[setKey] = currentStart;
                            currentStart += pageCountsRef.current[setKey] || 1;
                        });
                    }
                }
            });
            setStartingPages(newStarts);
        }
    };

    const handleViewModeChange = (mode: 'concept' | 'koreanOverview' | 'koreanAnalysis' | 'question' | 'answer') => {
        setViewMode(mode);
        pageCountsRef.current = {};
        setStartingPages({});
    };

    if (!isOpen) return null;

    const handlePrintAll = () => {
        // We select the main preview container containing all stacked sheets
        const printContent = document.getElementById('printable-preview-content');
        if (!printContent) return;

        const clone = printContent.cloneNode(true) as HTMLElement;
        clone.style.zoom = '1';
        clone.style.transform = 'none';

        // Find or create hidden iframe for printing
        let iframe = document.getElementById('print-iframe') as HTMLIFrameElement;
        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.id = 'print-iframe';
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            document.body.appendChild(iframe);
        }

        // Collect current CSS and web font links
        const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
            .map(el => {
                let html = el.outerHTML;
                // Solve local fonts references for print environment
                html = html.replace(/url\((['"]?)(?:\.\/|\.\.\/)?fonts\//g, 'url($1https://cdn.jsdelivr.net/npm/katex/dist/fonts/');
                return html;
            })
            .join('\n');

        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        doc.open();
        doc.write(`
            <html>
                <head>
                    <base href="${window.location.origin}/">
                    <title>${bookTitle} 시트 인쇄</title>
                    ${styles}
                    <style>
                        @page {
                            margin: 0 !important;
                            size: A4;
                        }
                        * {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        body {
                            background-color: white !important;
                            color: black !important;
                            font-family: 'Pretendard', -apple-system, sans-serif !important;
                            padding: 0 !important;
                            margin: 0 !important;
                        }
                        #printable-preview-content {
                            width: 100% !important;
                            height: auto !important;
                            padding: 0 !important;
                            margin: 0 !important;
                            display: block !important;
                            border: none !important;
                            box-shadow: none !important;
                        }
                        .A4-page {
                            width: 210mm !important;
                            height: 297mm !important;
                            min-width: 210mm !important;
                            min-height: 297mm !important;
                            box-sizing: border-box !important;
                            padding: 36px 68px 48px 68px !important;
                            page-break-after: always !important;
                            break-after: page !important;
                            background: white !important;
                            display: flex !important;
                            flex-direction: column !important;
                            position: relative !important;
                            border: none !important;
                            box-shadow: none !important;
                        }
                        .svg-diagram svg {
                            width: auto !important;
                            height: auto !important;
                            max-height: 114px !important;
                            display: block !important;
                            margin: 0 auto !important;
                        }
                    </style>
                </head>
                <body>
                    ${clone.outerHTML}
                </body>
            </html>
        `);
        doc.close();

        // Allow layout calculation, then trigger printing window
        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        }, 600);
    };

    return (
        <div className="fixed inset-0 bg-slate-950/95 z-[9999] flex flex-col overflow-hidden animate-in fade-in duration-200">
            {/* Header toolbar */}
            <div className="bg-slate-900 border-b border-slate-800 px-6 py-3.5 flex items-center justify-between shrink-0 select-none">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={onClose}
                        className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors"
                        title="닫기"
                    >
                        <X size={18} />
                    </button>
                    <div className="h-4 w-[1px] bg-slate-800" />
                    <span className="text-xs font-black text-slate-200 tracking-wide">{bookTitle} - {selectedParagraphs.length}개 유닛 인쇄 미리보기</span>
                </div>

                {/* Tabs */}
                <div className="flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800/80">
                    {(subject === '국어' || subject === 'korean') ? (
                        <>
                            <button
                                onClick={() => handleViewModeChange('koreanOverview')}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black transition-all ${
                                    viewMode === 'koreanOverview' 
                                        ? 'bg-indigo-600 text-white shadow-md' 
                                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/50'
                                }`}
                            >
                                <FileText size={12} />
                                <span>단락개요</span>
                            </button>
                            <button
                                onClick={() => handleViewModeChange('koreanAnalysis')}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black transition-all ${
                                    viewMode === 'koreanAnalysis' 
                                        ? 'bg-indigo-600 text-white shadow-md' 
                                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/50'
                                }`}
                            >
                                <FileText size={12} />
                                <span>단락분석</span>
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => handleViewModeChange('concept')}
                            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black transition-all ${
                                viewMode === 'concept' 
                                    ? 'bg-indigo-600 text-white shadow-md' 
                                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/50'
                            }`}
                        >
                            <FileText size={12} />
                            <span>개념 학습지</span>
                        </button>
                    )}
                    <button
                        onClick={() => handleViewModeChange('question')}
                        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black transition-all ${
                            viewMode === 'question' 
                                ? 'bg-indigo-600 text-white shadow-md' 
                                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/50'
                        }`}
                    >
                        <HelpCircle size={12} />
                        <span>실전 문항지</span>
                    </button>
                    <button
                        onClick={() => handleViewModeChange('answer')}
                        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black transition-all ${
                            viewMode === 'answer' 
                                ? 'bg-indigo-600 text-white shadow-md' 
                                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/50'
                        }`}
                    >
                        <Key size={12} />
                        <span>정답 및 해설지</span>
                    </button>
                </div>

                {/* Print and Zoom Actions */}
                <div className="flex items-center gap-4">
                    <div className="flex items-center bg-slate-950 border border-slate-800 rounded-lg p-0.5 text-slate-400 select-none">
                        <button 
                            onClick={() => setZoom(prev => Math.max(50, prev - 10))}
                            className="p-1 hover:text-white hover:bg-slate-900 rounded transition-colors"
                        >
                            <ZoomOut size={13} />
                        </button>
                        <span className="text-[10px] font-black px-2 w-10 text-center text-slate-300">{zoom}%</span>
                        <button 
                            onClick={() => setZoom(prev => Math.min(150, prev + 10))}
                            className="p-1 hover:text-white hover:bg-slate-900 rounded transition-colors"
                        >
                            <ZoomIn size={13} />
                        </button>
                    </div>

                    <button
                        onClick={handlePrintAll}
                        className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black shadow-lg transition-all hover:scale-105 active:scale-95"
                    >
                        <Printer size={13} />
                        <span>전체 인쇄하기</span>
                    </button>
                </div>
            </div>

            {/* Preview Sheet Area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden bg-slate-950 p-8 flex flex-col items-center custom-scrollbar">
                {/* Printable container wrapping all elements */}
                <div 
                    id="printable-preview-content"
                    className="flex flex-col gap-12 w-full max-w-[794px]"
                    style={{
                        zoom: zoom / 100,
                        transformOrigin: 'top center'
                    }}
                >
                    {selectedParagraphs.map((unit, idx) => {
                        const qSet = questionSets[unit.id];
                        const startPage = startingPages[unit.id] || 1;

                        if (viewMode === 'concept') {
                            const formattedConceptData = {
                                title: unit.title || '개념 핵심 요약',
                                description: unit.conceptDescription || '',
                                sections: unit.concepts || []
                            };

                            return (
                                <div key={unit.id} className="relative w-full">
                                    <UnitSheetView 
                                        conceptData={formattedConceptData}
                                        currentPage={idx + 1}
                                        fileName={bookTitle}
                                        onGenerate={() => {}}
                                        isLoading={false}
                                        zoom={100}
                                        inline={true}
                                        startingPageNumber={startPage}
                                        onPageCount={(c) => handlePageCount(unit.id, c)}
                                    />
                                    {/* Page Number indicator badge on preview screen only */}
                                    <div className="absolute top-2 left-2 bg-indigo-600/90 text-white font-black text-[9px] px-2 py-0.5 rounded shadow select-none print:hidden z-[99]">
                                        유닛 {idx + 1} / {selectedParagraphs.length} (시작 페이지: {startPage})
                                    </div>
                                </div>
                            );
                        } else if (viewMode === 'koreanOverview') {
                            return (
                                <div key={unit.id} className="relative w-full">
                                    <KoreanOverviewSheet 
                                        conceptData={unit}
                                        currentPage={idx + 1}
                                        fileName={bookTitle}
                                        onGenerate={() => {}}
                                        isLoading={false}
                                        zoom={100}
                                        inline={true}
                                    />
                                    <div className="absolute top-2 left-2 bg-indigo-600/90 text-white font-black text-[9px] px-2 py-0.5 rounded shadow select-none print:hidden z-[99]">
                                        유닛 {idx + 1} / {selectedParagraphs.length}
                                    </div>
                                </div>
                            );
                        } else if (viewMode === 'koreanAnalysis') {
                            return (
                                <div key={unit.id} className="relative w-full">
                                    <KoreanAnalysisSheet 
                                        conceptData={unit}
                                        currentPage={idx + 1}
                                        fileName={bookTitle}
                                        onGenerate={() => {}}
                                        isLoading={false}
                                        zoom={100}
                                        inline={true}
                                    />
                                    <div className="absolute top-2 left-2 bg-indigo-600/90 text-white font-black text-[9px] px-2 py-0.5 rounded shadow select-none print:hidden z-[99]">
                                        유닛 {idx + 1} / {selectedParagraphs.length}
                                    </div>
                                </div>
                            );
                        } else if (viewMode === 'question') {
                            const qSetsList = Array.isArray(qSet) ? qSet : (qSet ? [qSet] : []);
                            if (qSetsList.length === 0) {
                                return (
                                    <div key={unit.id} className="w-[794px] h-[300px] border border-dashed border-slate-800 rounded-xl flex flex-col items-center justify-center text-slate-500 gap-2 mx-auto">
                                        <HelpCircle size={32} />
                                        <span className="text-xs font-black">[{unit.title}] 유닛에 생성된 실전문항이 없습니다.</span>
                                    </div>
                                );
                            }
                            return (
                                <React.Fragment key={unit.id}>
                                    {qSetsList.map((singleSet, sIdx) => {
                                        const setKey = `${unit.id}_set_${sIdx}`;
                                        const startPage = startingPages[setKey] || 1;
                                        return (
                                            <div key={setKey} className="relative w-full">
                                                <QuestionSheetView 
                                                    questionSet={singleSet}
                                                    currentPage={idx + 1}
                                                    fileName={bookTitle}
                                                    onGenerate={() => {}}
                                                    isLoading={false}
                                                    zoom={100}
                                                    inline={true}
                                                    startingPageNumber={startPage}
                                                    onPageCount={(c) => handlePageCount(setKey, c)}
                                                />
                                                <div className="absolute top-2 left-2 bg-indigo-600/90 text-white font-black text-[9px] px-2 py-0.5 rounded shadow select-none print:hidden z-[99]">
                                                    유닛 {idx + 1} - 세트 {sIdx + 1} / {qSetsList.length} (시작 페이지: {startPage})
                                                </div>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        } else {
                            const qSetsList = Array.isArray(qSet) ? qSet : (qSet ? [qSet] : []);
                            if (qSetsList.length === 0) {
                                return (
                                    <div key={unit.id} className="w-[794px] h-[300px] border border-dashed border-slate-800 rounded-xl flex flex-col items-center justify-center text-slate-500 gap-2 mx-auto">
                                        <Key size={32} />
                                        <span className="text-xs font-black">[{unit.title}] 유닛에 생성된 해설이 없습니다.</span>
                                    </div>
                                );
                            }
                            return (
                                <React.Fragment key={unit.id}>
                                    {qSetsList.map((singleSet, sIdx) => {
                                        const setKey = `${unit.id}_set_${sIdx}`;
                                        const startPage = startingPages[setKey] || 1;
                                        return (
                                            <div key={setKey} className="relative w-full">
                                                <DetailAnswerView 
                                                    questionSet={singleSet}
                                                    currentPage={idx + 1}
                                                    fileName={bookTitle}
                                                    onGenerate={() => {}}
                                                    isLoading={false}
                                                    zoom={100}
                                                    inline={true}
                                                    startingPageNumber={startPage}
                                                    onPageCount={(c) => handlePageCount(setKey, c)}
                                                />
                                                <div className="absolute top-2 left-2 bg-indigo-600/90 text-white font-black text-[9px] px-2 py-0.5 rounded shadow select-none print:hidden z-[99]">
                                                    유닛 {idx + 1} - 세트 {sIdx + 1} / {qSetsList.length} (시작 페이지: {startPage})
                                                </div>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        }
                    })}
                </div>
            </div>
        </div>
    );
};

export default PdfSheetPreviewModal;
